import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { SideModal } from './SideModal'
import { FiLoader, FiRepeat, FiArrowRight } from 'react-icons/fi'

// Nova transferência entre contas (PRD §5.8).

interface Acc { id: number; name: string }
const inputCls = 'neu-convex w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'

// Os defaults opcionais permitem abrir o modal PRÉ-PREENCHIDO — usado pela
// prova real do Extrato: linha do banco sem par vira transferência em 1 clique
// (caso real: PIX entre contas próprias que o histórico do ERP não trouxe).
//
// movementId: veio de um movimento da conciliação bancária. Aí a transferência
// nasce JÁ VINCULADA ao movimento (que sai da fila de pendentes em vez de virar
// um título de despesa) — por isso o POST vai pro endpoint da conciliação. A
// ponta que o extrato define e o valor ficam travados: quem manda é o banco.
export function NovaTransferencia({ defaultFromId, defaultToId, defaultDate, defaultAmount, defaultDescription, movementId, onClose, onSaved }: {
  defaultFromId?: number; defaultToId?: number; defaultDate?: string; defaultAmount?: number; defaultDescription?: string
  movementId?: number
  // aviso: o backend pode ter VINCULADO o movimento a uma transferência que já
  // existia em vez de criar outra (a outra ponta do mesmo dinheiro já estava
  // lançada). Quem chama repassa o texto pra tela — sem isso o operador acha
  // que criou uma linha nova.
  onClose: () => void; onSaved: (aviso?: string) => void
}) {
  const { authenticatedFetch } = useAuth()
  const today = new Date().toISOString().slice(0, 10)
  const [accs, setAccs] = useState<Acc[]>([])
  const [from, setFrom] = useState(defaultFromId ? String(defaultFromId) : '')
  const [to, setTo] = useState(defaultToId ? String(defaultToId) : '')
  const [description, setDescription] = useState(defaultDescription || '')
  const [date, setDate] = useState(defaultDate || today)
  const [amount, setAmount] = useState(defaultAmount != null ? String(defaultAmount) : '')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  // Duplicata barrada pelo backend: só aparece o botão de confirmar depois que
  // ele explicou qual transferência já existe. Confirmar às cegas, antes do
  // aviso, é o que o guardrail está tentando impedir.
  const [duplicada, setDuplicada] = useState(false)
  // vindo da conciliação, a ponta que o extrato define e o valor não se editam
  const lockFrom = !!movementId && !!defaultFromId
  const lockTo = !!movementId && !!defaultToId
  const lockAmount = !!movementId && defaultAmount != null

  useEffect(() => {
    authenticatedFetch('/api/bank-accounts').then(r => r.json()).then(d => {
      if (d.success) setAccs(d.items.map((a: any) => ({ id: a.id, name: a.name })))
    }).catch(() => {})
  }, [authenticatedFetch])

  const save = async (confirmarDuplicada = false) => {
    if (!from || !to) { setErr('Selecione origem e destino.'); return }
    if (from === to) { setErr('Origem e destino devem ser diferentes.'); return }
    if (!description.trim()) { setErr('Informe a descrição.'); return }
    if (!(Number(amount) > 0)) { setErr('Informe um valor maior que zero.'); return }
    setSaving(true); setErr(null)
    try {
      const r = await authenticatedFetch(movementId ? `/api/reconciliation/${movementId}/transferencia` : '/api/transfers', {
        method: 'POST',
        body: JSON.stringify({
          from_account_id: Number(from), to_account_id: Number(to), description,
          transfer_date: date, amount: Number(amount),
          ...(confirmarDuplicada ? { confirmar_duplicada: true } : {}),
        }),
      })
      const d = await r.json()
      if (!d.success) { setErr(d.error || 'Falha ao registrar'); setDuplicada(!!d.duplicada); return }
      onSaved(d.aviso)
    } catch { setErr('Erro de conexão') }
    finally { setSaving(false) }
  }

  return (
    <SideModal
      title="Nova transferência"
      subtitle="Move saldo entre suas contas"
      icon={FiRepeat}
      maxWidth="max-w-md"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="neu-tab h-9 px-3 rounded-lg text-sm text-muted-foreground hover:bg-secondary">Cancelar</button>
          {duplicada && (
            <button onClick={() => save(true)} disabled={saving}
              title="Criar mesmo assim — só se forem dois movimentos diferentes de valor igual"
              className="neu-tab h-9 px-3 rounded-lg text-sm text-amber-600 dark:text-amber-400 hover:bg-secondary disabled:opacity-50">
              Criar assim mesmo
            </button>
          )}
          <button onClick={() => save()} disabled={saving}
            className="neu-relief inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">
            {saving && <FiLoader className="w-4 h-4 animate-spin" />} Transferir
          </button>
        </>
      }
    >
      {err && <div className="text-sm text-red-500">{err}</div>}
      {/* PLANOS (21/10/2026): campos em duas seções de papel sobre o chão da
          gaveta (contas / o movimento), campos h-9 e rótulo 13px. */}
      <section className="pln-secao">
      <h4 className="pln-secao-titulo">Contas</h4>
      <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
        <div>
          <label className="block text-[13px] font-medium text-foreground mb-1.5">Origem*</label>
          <select value={from} onChange={e => setFrom(e.target.value)} disabled={lockFrom}
            title={lockFrom ? 'Conta do extrato — definida pelo movimento do banco' : undefined}
            className={`${inputCls} h-9 ${lockFrom ? 'opacity-60 cursor-not-allowed' : ''}`}>
            <option value="">Selecione…</option>
            {accs.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        <FiArrowRight className="w-4 h-4 text-muted-foreground mb-2.5" />
        <div>
          <label className="block text-[13px] font-medium text-foreground mb-1.5">Destino*</label>
          <select value={to} onChange={e => setTo(e.target.value)} disabled={lockTo}
            title={lockTo ? 'Conta do extrato — definida pelo movimento do banco' : undefined}
            className={`${inputCls} h-9 ${lockTo ? 'opacity-60 cursor-not-allowed' : ''}`}>
            <option value="">Selecione…</option>
            {accs.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
      </div>
      </section>
      <section className="pln-secao">
      <h4 className="pln-secao-titulo">Movimento</h4>
      <div>
        <label className="block text-[13px] font-medium text-foreground mb-1.5">Descrição*</label>
        <input autoFocus value={description} onChange={e => setDescription(e.target.value)} className={`${inputCls} h-9`} placeholder="Ex.: Aporte no caixa" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[13px] font-medium text-foreground mb-1.5">Data*</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className={`${inputCls} h-9`} />
        </div>
        <div>
          <label className="block text-[13px] font-medium text-foreground mb-1.5">Valor*</label>
          <input type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)} readOnly={lockAmount}
            title={lockAmount ? 'Valor do movimento do banco — não editável na conciliação' : undefined}
            className={`${inputCls} h-9 ${lockAmount ? 'opacity-60 cursor-not-allowed' : ''}`} placeholder="0,00" />
        </div>
      </div>
      </section>
    </SideModal>
  )
}
